import { useMemo } from 'react'
import StandardDialog from 'components/UI/Dialog/StandardDialog'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import Box from '@mui/system/Box'
import sumBy from 'lodash-es/sumBy'
import COLORS from 'lib/colors'
import { AllocationRow } from './AllocationsTable'
import FreeTextMultiSelect from 'components/UI/FreeTextMultiSelect/FreeTextMultiSelect'
import omit from 'lodash-es/omit'
import FormWrapper from 'components/Formik/FormWrapper'
import * as yup from 'yup'
import { getIn, useFormikContext } from '../Formik/forms'
import { ContractRecord } from '../../lib/DataModel'
import { useCurrentUser } from 'lib/CurrentUserProvider'
import { splitTrimFilter, toDollars } from 'lib/utils'
import { sortBy } from 'lodash-es'
import { useOrbit } from 'providers/OrbitProvider'


const IOPreviewSchema = yup.object().shape({
  toEmails: yup.array()
    .of(
      yup.string()
        .email('Each To: Email must be a valid email address')
        .required('Email is required')
    )
    .required('To: Email is required')
    .min(1, 'There must be at least one To: Email'),
  ccEmails: yup.array()
    .of(
      yup.string()
        .email('Each CC: Email must be a valid email address')
        .required('Email is required')
    )
})

export interface RecipientData {
  toEmails: string[]
  ccEmails: string[]
}

interface IOEmailPreviewProps {
  monthText: string
  data: AllocationRow[]
}

function IOEmailPreview({monthText, data}: IOEmailPreviewProps) {
  const cap = (originalCap, adjustedCap) => adjustedCap || originalCap || 0
  const total = (originalCap, adjustedCap, cplInCents, budgetInCents) => budgetInCents || (cap(originalCap, adjustedCap) * (cplInCents || 0))

  const totalOriginalCap = sumBy(data, 'originalCap')
  const totalAdjustedCap = sumBy(data, row => cap(row.originalCap, row.adjustedCap))
  const totalCost = sumBy(data, row => total(row.originalCap, row.adjustedCap, row.cplInCents, row.budgetInCents))

  const sortedData = sortBy(data, (item) =>
    -total(item.originalCap, item.adjustedCap, item.cplInCents, item.budgetInCents)
  )

  const showCPEColumns = data.some(row => row['payout'] == 'cost_per_enroll')
  const showCPLColumns = data.some(row => ['cost_per_lead', 'variable_cost_per_lead'].includes(row['payout']))
  const showCapColumns = data.some(row => ['cost_per_lead', 'cost_per_enroll'].includes(row['payout']))

  const gridTemplateColumns = useMemo(() => {
    if(showCPLColumns && showCPEColumns) {
      return '3.5fr 120px .75fr 1.25fr 1fr 1fr 1.25fr'
    } else if(showCPEColumns) {
      return '3.5fr 120px .75fr 1.25fr 1.25fr'
    } else if(showCPLColumns) {
      return data.some(row => ['cost_per_lead'].includes(row['payout'])) ? '3.5fr 120px .75fr 1.25fr 1fr 1.25fr' : '3.5fr 120px 2fr 1.25fr'
    }
  },[showCPLColumns, showCPEColumns, data])

  return(
    <Box
      sx={{
        mt: -1,
        p: '12px 18px 18px 18px',
        borderRadius: '5px',
        border: `solid 1px ${COLORS.cadetGray}`,
        minHeight: 270,
        '& > p': {m: 0, mb: 1}
      }}
    >
      <p>Hello,</p>
      <p>Please see the following {monthText} IO. <a href="#" onClick={(e) => e.preventDefault()}>Click here to view Allocations in VEGA.</a></p>

      <Table
        aria-label="Insertion Order Line Items"
        sx={{
          mt: 3,
          '.MuiTableCell-root': { px: 0, py: 0.5, border: 'none' },
          '.MuiTableRow-root': {
            display: 'grid',
            gridTemplateColumns: `${gridTemplateColumns}`,
            columnGap: 4,
          }
        }}
      >
        <TableHead sx={{ '.MuiTableCell-root': { fontWeight: 'bold' } }}>
          <TableRow>
            <TableCell>Campaign Name</TableCell>
            <TableCell>Campaign ID</TableCell>
            {showCapColumns && <TableCell align="right">Cap</TableCell>}
            {showCapColumns && <TableCell align="right">Adj Cap</TableCell>}
            {showCPLColumns && <TableCell align="right">CPL</TableCell>}
            {showCPEColumns && <TableCell align="right">CPE</TableCell>}
            {showCPLColumns && <TableCell align="right">Total</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row) => (
            <TableRow
              key={row.clientCampaignName}
            >
              <TableCell component="th" scope="row" sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {row.clientCampaignName}
              </TableCell>
              <TableCell>{`CAMP-${row.clientCampaignPublicId}`}</TableCell>
              {showCapColumns &&
                <TableCell align="right">{row.payout != 'variable_cost_per_lead' && (row.originalCap ? row.originalCap : '—' )}</TableCell>
              }
              {showCapColumns &&
                <TableCell align="right">{row.payout != 'variable_cost_per_lead' && (row.adjustedCap ? row.adjustedCap : '—')}</TableCell>
              }
              {showCPLColumns &&
                <TableCell align="right">{(row.payout != 'cost_per_enroll') && (row.cplInCents ? toDollars(row.cplInCents) : '—')}</TableCell>
              }
              {showCPEColumns &&
                <TableCell align="right">{(row.payout == 'cost_per_enroll') && (row.cplInCents ? toDollars(row.cplInCents) : '—')}</TableCell>
              }
              {showCPLColumns &&
                <TableCell align="right">{(row.payout != 'cost_per_enroll') && ((row.cplInCents || row.budgetInCents) ? toDollars(total(row.originalCap, row.adjustedCap, row.cplInCents, row.budgetInCents)) : '—')}</TableCell>
              }
            </TableRow>
          ))}
          <TableRow sx={{ '.MuiTableCell-root': { fontWeight: 'bold', pt: 1 }, borderTop: `1px solid ${COLORS.charcoal}` }}>
            <TableCell component="th" scope="row">
              Total
            </TableCell>
            <TableCell align="right"></TableCell>
            {showCapColumns && <TableCell align="right">{totalOriginalCap}</TableCell>}
            {showCapColumns && <TableCell align="right">{totalAdjustedCap}</TableCell>}
            {showCPLColumns && <TableCell align="right">{'—'}</TableCell>}
            {showCPEColumns && <TableCell align="right">{'—'}</TableCell>}
            {showCPLColumns && <TableCell align="right">{totalCost ? toDollars(totalCost) : '—'}</TableCell>}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  )
}

interface SendIOPreviewDialogProps {
  onClose: () => void,
  contract: ContractRecord,
  data: AllocationRow[],
  month: moment.Moment
}

function SendIOPreviewDialog({onClose, contract, data, month}: SendIOPreviewDialogProps) {
  const { values, setFieldValue, errors, setErrors, submitForm, isSubmitting } = useFormikContext<RecipientData>()
  const { store } = useOrbit()

  const monthText = month.format('MMMM YYYY')
  const vendorName = getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.name')

  return(
    <StandardDialog
      title={`${monthText} IO`}
      description={<>Send the following insertion order for <strong>{monthText}</strong> to <strong>{vendorName}</strong>?</>}
      onCancel={onClose}
      maxWidth='920px'
      onConfirm={() => submitForm()}
      confirmText='Send'
      loading={isSubmitting}
    >
      <Stack direction='column' gap={2} >
        <Typography variant='h4'>Recipient(s)</Typography>
        <Stack direction='row' gap={2} >
          <FreeTextMultiSelect
            label="To: Email(s)"
            value={values.toEmails}
            setValue={(newValue) => setFieldValue('toEmails', newValue)}
            error={!!getIn(errors, 'toEmails')}
            onFocus={() => setErrors(omit(errors, ['toEmails']))}
            required={true}
            sx={{ flex: 3 }}
          />
          <FreeTextMultiSelect
            label="CC: Email(s)"
            value={values.ccEmails}
            setValue={(newValue) => setFieldValue('ccEmails', newValue)}
            error={!!getIn(errors, 'ccEmails')}
            onFocus={() => setErrors(omit(errors, ['ccEmails']))}
            sx={{ flex: 2 }}
          />
        </Stack>
        <Typography variant='h4'>Email Preview</Typography>
        <IOEmailPreview monthText={monthText} data={data}/>
      </Stack>
    </StandardDialog>
  )
}

interface SendIOPreviewContainerProps {
  onClose: () => void,
  contract: ContractRecord,
  data: AllocationRow[],
  onComplete: (values: RecipientData, { setSubmitting }: { setSubmitting: () => void; }) => void,
  month: moment.Moment
}

export default function SendIOPreviewContainer({onClose, contract, data, onComplete, month}: SendIOPreviewContainerProps) {
  const { currentUser } = useCurrentUser()
  const { store } = useOrbit()
  const currentUserEmail = getIn(currentUser, 'attributes.email') || ''
  const vendorContactEmail = getIn(contract, 'attributes.vendorContactEmail') || getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.contactEmail') || ''

  const initialRecipients: RecipientData = {
    toEmails: splitTrimFilter(vendorContactEmail, ','),
    ccEmails: splitTrimFilter(currentUserEmail, ','),
  }

  return(
    <FormWrapper data={initialRecipients} schema={IOPreviewSchema} onSubmit={onComplete}>
      <SendIOPreviewDialog onClose={onClose} contract={contract} data={data} month={month} />
    </FormWrapper>
  )
}
